//@ts-nocheck
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Stack, useMediaQuery, useTheme, FormControl } from "@mui/material";
import { GridColDef, GridPaginationModel, GridRowId, GridFilterModel } from "@mui/x-data-grid";
import { Table } from "../../molecules";
import { Button, Loading, MerchantSelector } from "../../atoms";
import {
  IMerchantBank,
  useGetMerchantBankList,
  MerchantBankPagingResponse,
  useMerchantBankDelete,
} from "../../../hooks/merchantBank";
import { useConfirmationModal, useGetMerchantsWithParentMerchant } from "../../../hooks";
import { useSetSnackBar } from "../../../store/Snackbar.state";
import { useUserInfo } from "../../../store/User.state";
import { RequestFilterValueTypeEnums, mapReactMUIToFilterOperationTypes, mapReactFilterValueTypeToRequestFilterValueType } from '../../../common/enums/RequestFilterEnums';
import { createBooleanOperator } from "../../molecules";
import { AddMerchantBankModal } from "./AddMerchantBankModal";
import EditIcon from "@mui/icons-material/Edit"; // Import EditIcon
import DeleteIcon from "@mui/icons-material/Delete"; // Import DeleteIcon
import { GridActionsCellItem } from "@mui/x-data-grid"; // Import GridActionsCellItem
import { EditMerchantBankModal } from "./EditMerchantBankModal"; // Import EditMerchantBankModal
import { downloadExcel } from "../../../util/downloadExcel"; // Import downloadExcel
import dayjs from "dayjs";

type MerchantIBANListingTableProps = {
  onRowClick?: (data: { id: GridRowId; row: IMerchantBank }) => void;
  refreshTrigger?: number; // New prop to trigger refresh
};

const convertCurrencyCodeToSymbol = (code: number) => {
  switch (code) {
    case 949:
      return "TRY";
    case 840:
      return "USD";
    case 978:
      return "EUR";
    default:
      return "";
  }
};

export const MerchantIBANListingTable = ({
  onRowClick,
  refreshTrigger,
}: MerchantIBANListingTableProps) => {
  const theme = useTheme();
  const [userInfo] = useUserInfo();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [queryOptions, setQueryOptions] = useState<{ filters?: any[] }>({});
  const [tableData, setTableData] = useState<MerchantBankPagingResponse>();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false); // State for edit modal
  const [editingRowData, setEditingRowData] = useState<IMerchantBank | null>(null); // State for row being edited

  const { mutate: getMerchantBankList, isLoading } = useGetMerchantBankList();
  const { mutate: merchantBankDelete, isLoading: isDeleting } = useMerchantBankDelete();
  const { showConfirmationModal } = useConfirmationModal();
  const setSnackbar = useSetSnackBar();

  const [dynamicFilters, setDynamicFilter] = useState([]);
  const previousQueryParams = React.useRef({});

  const { data: rawMerchantList } = useGetMerchantsWithParentMerchant(null);
  const [selectedMerchants, setSelectedMerchants] = useState<number[]>([]);
  const [appliedMerchants, setAppliedMerchants] = useState<number[]>([]);

  const merchantList = useMemo(() => {
    return rawMerchantList?.data?.map(
      (rawPosType: { merchantName: string; merchantId: number }) => {
        return {
          label: `${rawPosType.merchantName} (${rawPosType.merchantId})`,
          value: rawPosType.merchantId,
        };
      }
    );
  }, [rawMerchantList?.data]);

  useEffect(() => {
    const savedFilter = localStorage.getItem("merchantIBANListingTableFilter");
    if (savedFilter) {
      const parsed = JSON.parse(savedFilter);
      if (parsed.filters && Array.isArray(parsed.filters)) {
        setQueryOptions(parsed);
      }
    }
  }, []);

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  const convertTableFiltersToRequestFilter = (tableFilters) => {
    let convertedFilters = [];
    tableFilters.items.map((item) => {
      var findColumn = columns.find(x => x.field == item.field);
      convertedFilters.push({
        field: item.field,
        values: [item.value.toString()],
        valueType: mapReactFilterValueTypeToRequestFilterValueType(item.value, findColumn?.columnType),
        operationType: mapReactMUIToFilterOperationTypes(item.operator)
      });
    });
    return convertedFilters;
  }

  const handleFilterChange = debounce((filters, tableFilters) => {
    if (filters.clearFilter) {
      setDynamicFilter([]);
      localStorage.removeItem('merchantIBANListingTableFilter')
      return setQueryOptions({});
    }

    const queryParams = filters;

    const isValueValid = (key, value) => {
      // Customize validation logic if needed, for now, all values are valid
      return value !== undefined && value !== null && value !== '';
    }

    const allValuesValid = Object.entries(queryParams).every(([key, value]) => isValueValid(key, value));

    const previousKeysLength = Object.keys(previousQueryParams.current).length;
    const currentKeysLength = Object.keys(queryParams).length;

    if (JSON.stringify(previousQueryParams.current) === JSON.stringify(queryParams)) {
      return;
    }

    if (allValuesValid || (previousKeysLength > currentKeysLength)) {
      setQueryOptions(queryParams);
      setDynamicFilter(convertTableFiltersToRequestFilter(tableFilters));
      localStorage.setItem('merchantIBANListingTableFilter', JSON.stringify(queryParams));
    }

    previousQueryParams.current = queryParams;

  }, 500);

  const refreshData = useCallback(() => {
    const filters = [...(dynamicFilters || [])];

    if (appliedMerchants && appliedMerchants.length > 0) {
      filters.push({
        field: "merchantId",
        values: appliedMerchants.map(String),
        valueType: RequestFilterValueTypeEnums.NUMBER,
        operationType: 1
      });
    }

    const requestPayload = {
      size: paginationModel.pageSize,
      page: paginationModel.page,
      orderBy: "CreateDate",
      orderByDesc: true,
      merchantId: userInfo ? Number(userInfo.merchantId) : undefined,
      filters: filters,
    };

    getMerchantBankList(requestPayload, {
      onSuccess: (data) => {
        if (data.isSuccess) {
          setTableData(data?.data);
        } else {
          setSnackbar({
            severity: "error",
            description: data.message,
            isOpen: true,
          });
        }
      },
      onError: () => {
        setSnackbar({
          severity: "error",
          description: "İşlem sırasında bir hata oluştu",
          isOpen: true,
        });
      },
    });
  }, [getMerchantBankList, paginationModel.pageSize, paginationModel.page, userInfo, dynamicFilters, setSnackbar, appliedMerchants]);

  useEffect(() => {
    refreshData();
  }, [refreshData, refreshTrigger]);

  const handleChangePagination = (model: GridPaginationModel) => {
    setPaginationModel(model);
    setTableData(undefined);
  };

  const handleEditClick = useCallback((row: IMerchantBank) => {
    setEditingRowData(row);
    setIsEditModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((row: IMerchantBank) => {
    showConfirmationModal({
      title: "Banka Hesabını Sil",
      description: `"${row.merchantName}" adlı işyerinin IBAN kaydını silmek istediğinizden emin misiniz?`,
      onConfirm: () => {
        merchantBankDelete(row.id, {
          onSuccess: (res) => {
            if (res.isSuccess) {
              setSnackbar({
                severity: "success",
                description: "IBAN kaydı başarıyla silindi.",
                isOpen: true,
              });
              refreshData(); // Refresh table after deletion
            } else {
              setSnackbar({
                severity: "error",
                description: res.message || "IBAN kaydı silinirken bir hata oluştu.",
                isOpen: true,
              });
            }
          },
          onError: () => {
            setSnackbar({
              severity: "error",
              description: "IBAN kaydı silinirken bir hata oluştu.",
              isOpen: true,
            });
          },
        });
      },
    });
  }, [merchantBankDelete, setSnackbar, refreshData, showConfirmationModal]);

  const currencyOptions = [
    { label: "TRY", value: 949 },
    { label: "USD", value: 840 },
    { label: "EUR", value: 978 },
  ];

  const columns: GridColDef[] = useMemo(() => {
    return [
      { field: "merchantId", headerName: "İşyeri No", width: 120, filterable: true, columnType: RequestFilterValueTypeEnums.NUMBER },
      { field: "merchantName", headerName: "İşyeri Adı", width: 250, filterable: false, columnType: RequestFilterValueTypeEnums.STRING },
      { field: "taxNumber", headerName: "Vergi No", width: 150, filterable: false, columnType: RequestFilterValueTypeEnums.STRING },
      { field: "iban", headerName: "IBAN 1", width: 250, filterable: true, columnType: RequestFilterValueTypeEnums.STRING },
      { field: "bankName", headerName: "Banka 1", width: 200, filterable: false, columnType: RequestFilterValueTypeEnums.STRING },
      { field: "accountOwner", headerName: "Hesap Sahibi 1", width: 150, filterable: true, columnType: RequestFilterValueTypeEnums.STRING },
      {
        field: "currencyCode",
        headerName: "Para Birimi 1",
        width: 120,
        renderCell: (params) => convertCurrencyCodeToSymbol(params.value),
        filterOperators: [createBooleanOperator(currencyOptions)],
        columnType: RequestFilterValueTypeEnums.NUMBER
      },
      { field: "iban1", headerName: "IBAN 2", width: 250, filterable: true, columnType: RequestFilterValueTypeEnums.STRING },
      { field: "bankName1", headerName: "Banka 2", width: 200, filterable: false, columnType: RequestFilterValueTypeEnums.STRING },
      { field: "accountOwner1", headerName: "Hesap Sahibi 2", width: 150, filterable: true, columnType: RequestFilterValueTypeEnums.STRING },
      {
        field: "currencyCode1",
        headerName: "Para Birimi 2",
        width: 120,
        renderCell: (params) => convertCurrencyCodeToSymbol(params.value),
        filterOperators: [createBooleanOperator(currencyOptions)],
        columnType: RequestFilterValueTypeEnums.NUMBER
      },
      { field: "iban2", headerName: "IBAN 3", width: 250, filterable: true, columnType: RequestFilterValueTypeEnums.STRING },
      { field: "bankName2", headerName: "Banka 3", width: 200, filterable: false, columnType: RequestFilterValueTypeEnums.STRING },
      { field: "accountOwner2", headerName: "Hesap Sahibi 3", width: 150, filterable: true, columnType: RequestFilterValueTypeEnums.STRING },
      {
        field: "currencyCode2",
        headerName: "Para Birimi 3",
        width: 120,
        renderCell: (params) => convertCurrencyCodeToSymbol(params.value),
        filterOperators: [createBooleanOperator(currencyOptions)],
        columnType: RequestFilterValueTypeEnums.NUMBER
      },
      {
        field: "status", headerName: "Durum", width: 120, filterable: true, columnType: RequestFilterValueTypeEnums.STRING,
        valueFormatter: (params) => (params.value === "ACTIVE" ? "Aktif" : "Pasif")
      },
      { field: "updatedBy", headerName: "Güncelleyen Kullanıcı", width: 150, filterable: true, columnType: RequestFilterValueTypeEnums.STRING },
      {
        field: "updatedAt",
        headerName: "Güncellenme Tarihi",
        width: 200,
        renderCell: (params) => {
          if (!params.value) return "";
          const formattedDate = dayjs(params.value).format("DD.MM.YYYY HH:mm");
          if (formattedDate === "Invalid Date") return "";
          return formattedDate;
        }
      },
    ];
  }, [handleEditClick, handleDeleteClick]);

  const hasActiveFilters = dynamicFilters && dynamicFilters.length > 0;

  const keyMapping = {
    status: "Durum",
    currencyCode: "Para Birimi 1",
    currencyCode1: "Para Birimi 2",
    currencyCode2: "Para Birimi 3",
  };

  const onSave = () => {
    const filters = [...(dynamicFilters || [])];

    if (appliedMerchants && appliedMerchants.length > 0) {
      filters.push({
        field: "merchantId",
        values: appliedMerchants.map(String),
        valueType: RequestFilterValueTypeEnums.NUMBER,
        operationType: 1
      });
    }

    const requestPayload = {
      size: tableData?.totalItems ?? 9999,
      page: 0,
      orderBy: "CreateDate",
      orderByDesc: true,
      merchantId: userInfo ? Number(userInfo.merchantId) : undefined,
      filters: filters,
    };

    getMerchantBankList(requestPayload, {
      onSuccess: (data) => {
        if (data.isSuccess) {
          function fieldValue(data) {
            return data?.map((item) => {
              return {
                "İşyeri No": item?.merchantId,
                "İşyeri Adı": item?.merchantName,
                "Vergi No": item?.taxNumber,
                "IBAN 1": item?.iban,
                "Banka 1": item?.bankName,
                "Hesap Sahibi 1": item?.accountOwner,
                [keyMapping.currencyCode]: convertCurrencyCodeToSymbol(item?.currencyCode),
                "IBAN 2": item?.iban1,
                "Banka 2": item?.bankName1,
                "Hesap Sahibi 2": item?.accountOwner1,
                [keyMapping.currencyCode1]: convertCurrencyCodeToSymbol(item?.currencyCode1),
                "IBAN 3": item?.iban2,
                "Banka 3": item?.bankName2,
                "Hesap Sahibi 3": item?.accountOwner2,
                "Güncelleyen Kullanıcı": item?.updatedBy,
                "Güncellenme Tarihi": item?.updatedAt,
                [keyMapping.currencyCode2]: convertCurrencyCodeToSymbol(item?.currencyCode2),
                [keyMapping.status]: item?.status === "ACTIVE" ? "Aktif" : "Pasif",
              };
            });
          }
          const refactoredData = fieldValue(data?.data?.result);
          downloadExcel(refactoredData || [], "Üye İşyeri IBAN Listesi", keyMapping);
        } else {
          setSnackbar({
            severity: "error",
            description: data.message,
            isOpen: true,
          });
        }
      },
      onError: () => {
        setSnackbar({
          severity: "error",
          description: "İşlem sırasında bir hata oluştu",
          isOpen: true,
        });
      },
    });
  };

  return (
    <>
      {(isLoading || isDeleting) && <Loading />}
      <Stack flex={1} p={2}>
        {merchantList && merchantList.length > 0 && (
          <Stack direction="row" spacing={2} mb={2}>
            <FormControl sx={{ width: 400 }}>
              <MerchantSelector
                field={{
                  value: selectedMerchants,
                  onChange: setSelectedMerchants
                }}
                merchantList={merchantList}
              />
            </FormControl>
            <Button
              variant="contained"
              text="ARA"
              onClick={() => setAppliedMerchants(selectedMerchants)}
              sx={{ height: 56 }}
            />
          </Stack>
        )}
        <Table
          columnVisibilityConfigKey="merchantIBANListingTableColumnVisibilityKey"
          filterKey="merchantIBANListingTableFilter"
          handleFilterChange={handleFilterChange}
          hasActiveFilters={hasActiveFilters}
          paginationModel={paginationModel}
          onPaginationModelChange={handleChangePagination}
          paginationMode="server"
          filterMode="server"
          rowCount={tableData?.totalItems || 0}
          sx={{ width: isDesktop ? "100%" : window.innerWidth - 50 }}
          onRowClick={onRowClick}
          rows={tableData?.result ?? []}
          columns={columns}
          exportFileName="Üye İşyeri IBAN Listesi"
          onSave={() => onSave()}
        />
      </Stack>
      <EditMerchantBankModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSaveSuccess={refreshData}
        initialData={editingRowData}
      />
    </>
  );
};